function removeExistingOverlay() {
    const existing = document.querySelector('.invoice-pdf-overlay');
    if (existing) {
        existing.remove();
    }
}

function attachOverlayCloseHandlers(overlay, close) {
    overlay.querySelector('.invoice-pdf-close').addEventListener('click', close);
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay) {
            close();
        }
    });
    document.body.appendChild(overlay);
}

function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function isImagePreview(name, mimeType) {
    if (mimeType?.startsWith('image/')) {
        return true;
    }
    return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(name || '');
}

export function openPdfResponse(response, { title = 'PDF', onClose, downloadFileName } = {}) {
    if (!response) {
        return null;
    }
    const pdfBlob = response instanceof Blob
        ? response
        : new Blob([response], { type: 'application/pdf' });
    return openPdfInApp(pdfBlob, { title, onClose, downloadFileName });
}

export function openPdfInApp(blob, { title = 'Invoice PDF', onClose, downloadFileName } = {}) {
    if (!blob) {
        return null;
    }

    const pdfBlob = blob instanceof Blob ? blob : new Blob([blob], { type: 'application/pdf' });
    const fileURL = URL.createObjectURL(pdfBlob);

    removeExistingOverlay();

    const overlay = document.createElement('div');
    overlay.className = 'invoice-pdf-overlay';
    overlay.innerHTML = `
        <div class="invoice-pdf-panel" role="dialog" aria-label="${escapeHtml(title)}">
            <div class="invoice-pdf-toolbar">
                <span class="invoice-pdf-title">${escapeHtml(title)}</span>
                <div class="invoice-pdf-toolbar-actions">
                    ${downloadFileName ? '<button type="button" class="invoice-pdf-download btn btn-outline-light">Download</button>' : ''}
                    <button type="button" class="invoice-pdf-close btn btn-primary">Back to application</button>
                </div>
            </div>
            <iframe class="invoice-pdf-frame" src="${fileURL}" title="${escapeHtml(title)}"></iframe>
        </div>
    `;

    const close = () => {
        URL.revokeObjectURL(fileURL);
        overlay.remove();
        onClose?.();
    };

    attachOverlayCloseHandlers(overlay, close);
    const downloadButton = overlay.querySelector('.invoice-pdf-download');
    if (downloadButton && downloadFileName) {
        downloadButton.addEventListener('click', () => {
            const link = document.createElement('a');
            link.href = fileURL;
            link.download = downloadFileName;
            link.click();
        });
    }

    return close;
}

export function openFileInApp(source, { title = 'File Preview', fileName, downloadFileName, onClose } = {}) {
    if (!source) {
        return null;
    }

    let fileURL = null;
    let revokeOnClose = false;
    let displayName = fileName || title;
    let mimeType = '';

    if (source instanceof File || source instanceof Blob) {
        fileURL = URL.createObjectURL(source);
        revokeOnClose = true;
        displayName = source.name || displayName;
        mimeType = source.type || '';
    } else if (typeof source === 'string') {
        fileURL = source;
    } else {
        return null;
    }

    removeExistingOverlay();

    const safeTitle = escapeHtml(displayName);
    const isImage = isImagePreview(displayName, mimeType);
    const bodyContent = isImage
        ? `<div class="file-preview-image-wrap"><img class="file-preview-image" src="${fileURL}" alt="${safeTitle}" /></div>`
        : `<iframe class="invoice-pdf-frame" src="${fileURL}" title="${safeTitle}"></iframe>`;

    const overlay = document.createElement('div');
    overlay.className = 'invoice-pdf-overlay';
    overlay.innerHTML = `
        <div class="invoice-pdf-panel" role="dialog" aria-label="${safeTitle}">
            <div class="invoice-pdf-toolbar">
                <span class="invoice-pdf-title">${safeTitle}</span>
                <div class="invoice-pdf-toolbar-actions">
                    ${downloadFileName ? '<button type="button" class="invoice-pdf-download btn btn-outline-light">Download</button>' : ''}
                    <button type="button" class="invoice-pdf-close btn btn-primary">Back to application</button>
                </div>
            </div>
            ${bodyContent}
        </div>
    `;

    const close = () => {
        if (revokeOnClose && fileURL) {
            URL.revokeObjectURL(fileURL);
        }
        overlay.remove();
        onClose?.();
    };

    attachOverlayCloseHandlers(overlay, close);
    const downloadButton = overlay.querySelector('.invoice-pdf-download');
    if (downloadButton && downloadFileName) {
        downloadButton.addEventListener('click', () => {
            const link = document.createElement('a');
            link.href = fileURL;
            link.download = downloadFileName;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.click();
        });
    }

    return close;
}

export function openHtmlInApp(html, { title = 'Preview', onClose } = {}) {
    if (!html) {
        return null;
    }

    removeExistingOverlay();

    const overlay = document.createElement('div');
    overlay.className = 'invoice-pdf-overlay';
    overlay.innerHTML = `
        <div class="invoice-pdf-panel" role="dialog" aria-label="${escapeHtml(title)}">
            <div class="invoice-pdf-toolbar">
                <span class="invoice-pdf-title">${escapeHtml(title)}</span>
                <div class="invoice-pdf-toolbar-actions">
                    <button type="button" class="invoice-pdf-print btn btn-outline-light">Print</button>
                    <button type="button" class="invoice-pdf-close btn btn-primary">Back to application</button>
                </div>
            </div>
            <iframe class="invoice-pdf-frame" title="${escapeHtml(title)}"></iframe>
        </div>
    `;

    const frame = overlay.querySelector('.invoice-pdf-frame');
    frame.srcdoc = html;

    const close = () => {
        overlay.remove();
        onClose?.();
    };

    attachOverlayCloseHandlers(overlay, close);
    overlay.querySelector('.invoice-pdf-print').addEventListener('click', () => {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
    });

    return close;
}
